
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface UserProfileData {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  avatar_url: string;
  preferred_area: string;
  preferred_property_type: string;
  budget_min: number;
  budget_max: number;
  notes: string;
}

export const useUserProfile = () => {
  const [profile, setProfile] = useState<UserProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const { toast } = useToast();

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setProfile(null);
        setLoading(false);
        return;
      }

      console.log('Fetching profile for user:', user.id);

      // محاولة جلب البيانات من جدول الباحثين أولاً
      const { data, error } = await supabase
        .from('tenants')
        .select('*')
        .eq('user_id', user.id)
        .single();
        
      if (error && error.code === 'PGRST116') {
        console.log('Profile not found, creating new one...');
        // إذا لم يتم العثور على الملف الشخصي، قم بإنشائه
        const newProfile = {
          user_id: user.id,
          full_name: user.user_metadata?.full_name || '',
          email: user.email || '',
          phone: user.user_metadata?.phone || '',
          avatar_url: '',
          preferred_area: '',
          preferred_property_type: '',
          budget_min: 0,
          budget_max: 0,
          notes: ''
        };
        
        const { data: insertData, error: insertError } = await supabase
          .from('tenants')
          .insert(newProfile)
          .select()
          .single();
          
        if (insertError) {
          console.error('Error creating profile:', insertError);
          toast({
            title: "خطأ",
            description: "حدث خطأ في إنشاء الملف الشخصي",
            variant: "destructive",
          });
        } else {
          console.log('Profile created successfully:', insertData);
          setProfile(insertData);
        }
      } else if (error) {
        console.error('Error fetching profile:', error);
        toast({
          title: "خطأ",
          description: "حدث خطأ في جلب البيانات",
          variant: "destructive",
        });
      } else {
        console.log('Profile loaded successfully:', data);
        setProfile(data);
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ غير متوقع",
        variant: "destructive",
      });
    }
    setLoading(false);
  };

  const updateProfile = async (updates: Partial<UserProfileData>) => {
    if (!profile) return;

    setSaveLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      console.log('Updating profile with:', updates);

      const { data, error } = await supabase
        .from('tenants')
        .update(updates)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating profile:', error);
        toast({
          title: "خطأ",
          description: "حدث خطأ في حفظ البيانات",
          variant: "destructive",
        });
      } else {
        console.log('Profile updated successfully:', data);
        setProfile(data);
        toast({
          title: "تم الحفظ",
          description: "تم حفظ التغييرات بنجاح",
        });
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ في حفظ البيانات",
        variant: "destructive",
      });
    }
    setSaveLoading(false);
  };

  const handleInputChange = (field: keyof UserProfileData, value: string | number) => {
    if (profile) {
      const updatedProfile = {
        ...profile,
        [field]: value
      };
      console.log('Updating field:', field, 'with value:', value);
      setProfile(updatedProfile);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  return {
    profile,
    loading,
    saveLoading,
    updateProfile,
    handleInputChange,
    fetchProfile
  };
};
